import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { TimerReset } from "lucide-react";
import type { OeeResponse } from "@opus-dt/shared-types";
import { cn } from "@/lib/utils";
import { useOeeThresholds, type OeeThresholds } from "@/hooks/useOrganizationPolicy";
import { thresholdColor } from "./oee-thresholds";
import { PlannedBasisBadge } from "./PlannedBasisBadge";
import { OverrunBadge, isOverrun } from "./OverrunBadge";

interface Props {
  oee: OeeResponse;
  lineId: string;
}

interface BarRow {
  key: string;
  label: string;
  value: number | null;
}

// A terceira coluna é FIXA, não `auto`: cada `Bar` é um grid próprio, então uma coluna que se
// dimensiona pelo conteúdo daria a cada linha uma pista (`1fr`) de largura diferente — "—",
// "100.0%" e "125.0% +25 pp" produziriam três réguas distintas, e barras de mesmo valor deixariam
// de desenhar igual num bloco cujo propósito é comparação lado a lado. 96px cabe valor + badge.
const ROW = "grid grid-cols-[110px_1fr_96px] items-center gap-3 text-[12px]";
const LABEL = "text-text-secondary";
const BAR_TRACK = "h-1.5 overflow-hidden rounded-full bg-bg-muted-rd";
const BAR_FILL = "h-full w-[var(--w)] rounded-full transition-all";
const VALUE = "font-mono text-right text-text-primary";

function fmtPct(v: number | null): string {
  return v == null ? "—" : `${v.toFixed(1)}%`;
}

function Bar({ row, thresholds }: { row: BarRow; thresholds: OeeThresholds }): React.ReactElement {
  const pct = row.value == null ? 0 : Math.max(0, Math.min(100, row.value));
  const over = isOverrun(row.value);
  return (
    <div className={ROW} role="group" aria-label={row.label}>
      <span className={LABEL}>{row.label}</span>
      <div className={BAR_TRACK}>
        <div
          // Barra cheia com a ponta direita QUADRADA quando o valor passa de 100%: lê
          // como "continua além da pista", em vez de terminar arredondada como quem chegou no fim.
          className={cn(BAR_FILL, thresholdColor(row.value, thresholds), over && "rounded-r-none")}
          style={{ "--w": `${pct}%` } as CSSProperties}
          role="progressbar"
          // `aria-valuenow` fica na escala da barra (0–100) e o valor REAL vai em `aria-valuetext`.
          // Anunciar 125 com `aria-valuemax=100` é estado inválido, e leitor de tela nenhum sabe o
          // que fazer com isso.
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={fmtPct(row.value)}
          aria-label={row.label}
        />
      </div>
      <span className={cn(VALUE, "inline-flex items-center justify-end gap-1 whitespace-nowrap")}>
        {fmtPct(row.value)}
        <OverrunBadge value={row.value} metricLabel={row.label} />
      </span>
    </div>
  );
}

export function OeeBreakdown({ oee, lineId }: Props) {
  // Spec 079 — a régua de cor é da organização. Uma leitura para as cinco barras.
  const thresholds = useOeeThresholds();
  const adherenceValue = oee.planAdherence?.value ?? null;
  // O aviso fala em "expediente" e em "Disponibilidade acima de 100%", então só pode aparecer quando
  // as DUAS coisas são verdade. Sem esse gate ele contradiz o próprio card: numa linha sem
  // calendário (`basis: "run"`) a run que passa do `expectedEnd` já produz excedente, e o texto
  // afirmaria um expediente que o crachá logo abaixo nega ("o expediente da linha não cobre esta
  // janela"); e com `plannedMinutes = 0` a Disponibilidade é NULL e renderiza "—", enquanto o texto
  // explicaria por que ela passou de 100%.
  const overrunMinutes =
    oee.plannedBasis === "calendar" && oee.availability != null
      ? Math.max(0, oee.raw.operatingMinutes - oee.raw.plannedMinutes)
      : 0;
  const showAdherence = adherenceValue != null;

  const rows: BarRow[] = [
    { key: "oee", label: "OEE", value: oee.oee },
    { key: "availability", label: "Disponibilidade", value: oee.availability },
    { key: "performance", label: "Performance", value: oee.performance },
    { key: "quality", label: "Qualidade", value: oee.quality },
  ];

  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <Bar key={r.key} row={r} thresholds={thresholds} />
      ))}

      {}
      {showAdherence ? (
        <>
          <div className="h-2" aria-hidden="true" />
          <Bar
            row={{ key: "adherence", label: "Aderência", value: adherenceValue }}
            thresholds={thresholds}
          />
        </>
      ) : null}

      {/* O excedente em MINUTOS, porque "+25 pp" diz o tamanho relativo e não
          o tamanho absoluto. Quem opera a linha raciocina em horas de turno, não em pontos. */}
      {overrunMinutes > 0 ? (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-rd-warning-700">
          <TimerReset className="h-3 w-3 shrink-0" aria-hidden />
          Hora extra: {overrunMinutes} min além do expediente — é por isso que a Disponibilidade
          passa de 100%.
        </p>
      ) : null}

      {/* A base do tempo planejado fica junto das barras, não escondida num diálogo: é
          o que impede a Disponibilidade de ser lida contra um expediente que ninguém cadastrou. */}
      <PlannedBasisBadge
        basis={oee.plannedBasis}
        calendarSource={oee.calendarSource}
        lineId={lineId}
        className="mt-3"
      />

      {}
      <p className="mt-4 text-[12px] text-text-tertiary">
        Comparado às últimas 24h.{" "}
        <Link
          to={`/lines/${lineId}/detail`}
          className="text-text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
        >
          Ver série temporal completa
        </Link>
      </p>
    </div>
  );
}
